import type { WebphoneCallState } from '@modules/SoftPhoneModule/types';
import type { ITask } from '../types';

type LiveCallState = {
  /** The task the CSR currently has selected in the Inbox. */
  selectedTaskId?: string;
  taskById: Readonly<
    Record<string, Pick<ITask, 'id' | 'attributes'> | null | undefined>
  >;
  callList: readonly Pick<
    WebphoneCallState,
    'callId' | 'established' | 'hasSession'
  >[];
};

/**
 * Create Customer's live-call rule as a pure function: a task's call is live
 * only while that task is the selected one, is a call task (its id is its
 * `sessionSid`), carries a `callId`, and that call is on the softphone either
 * established or with a session (a ringing outbound call counts).
 *
 * @param taskId The task to test.
 * @param state The selection, task and softphone data to test against.
 * @returns The task id (the call's SID) while the call is live, otherwise undefined.
 */
export function liveCallSid(
  taskId: string | undefined,
  state: LiveCallState,
): string | undefined {
  if (!taskId || taskId !== state.selectedTaskId) {
    return undefined;
  }

  const task = state.taskById[taskId];

  if (!task || task.id !== task.attributes.sessionSid) {
    return undefined;
  }

  const callId = task.attributes?.callId;
  if (!callId) {
    return undefined;
  }

  const call = state.callList.find((c) => c.callId === callId);
  const isCallActive = call && (call.established || call.hasSession);

  return isCallActive ? task.id : undefined;
}
